import { computed, inject, provide } from 'vue';

import type { InjectionKey, Ref } from 'vue';

interface PortalContext {
  target: Ref<HTMLElement | null>;
  boundary: Ref<HTMLElement | null>;
}

const portalKey: InjectionKey<PortalContext> = Symbol('ui-portal');

/**
 * Where the kit's overlays (`UiBottomSheet`, `UiDialog`, `UiMenu`) mount, and
 * what a popover must stay inside. The app shell provides the element that
 * stands for the screen — on a wide window that is the phone frame, so a
 * sheet opens inside the frame instead of over the whole window — and, while
 * the screen is narrower than the window, the box that clips it. Without a
 * provider, or before the elements are mounted, overlays mount on `body` and
 * popovers keep to the window.
 */
export const providePortalTarget = (target: Ref<HTMLElement | null>, boundary: Ref<HTMLElement | null>) => {
  provide(portalKey, { target, boundary });
};

/**
 * Call in `<script setup>` and bind the result: `:to="portalTarget"`. The
 * provided element, else `body`.
 */
export const usePortalTarget = () => {
  const context = inject(portalKey, null);

  return computed<HTMLElement | 'body'>(() => context?.target.value ?? 'body');
};

/**
 * Call in `<script setup>` and bind the result on a popover's content:
 * `:collision-boundary="portalBoundary"`. The provided clipping box, else none
 * (the window).
 */
export const usePortalBoundary = () => {
  const context = inject(portalKey, null);

  return computed<HTMLElement[]>(() => (context?.boundary.value ? [context.boundary.value] : []));
};
